import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import {
 Questionario,
 QuestionarioDetalhado,
 CreateQuestionarioDTO,
 VincularCapituloDTO,
 QuestionarioCapitulo,
 Pergunta,
 CreateVersaoRespostasDTO,
 VersaoRespostas,
 SalvarRespostaDTO,
 Resposta,
 CreateTitularDTO,
 Titular,
 TitularDetalhado,
 UpdateStatusVersaoDTO,
 RelatorioMapeamento,
 Area,
 Dado,
 FiltroQuestionarios,
 FiltroTitulares,
 FiltroAreas,
 FiltroDados,
 PaginationMeta,
 EstatisticasGerais
} from '../../types/dashboard/mapeamentos.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const MAPEAMENTOS_API_PREFIX = '/api/dashboard/mapeamentos';

interface RequestConfig {
 timeout?: number;
}

interface ApiEstatisticasResponse {
 success?: boolean;
 data?: Partial<EstatisticasGerais>;
 message?: string;
 timestamp?: string;
 [key: string]: any;
}

interface ApiRelatorioResponse {
 success?: boolean;
 data?: any;
 metadata?: {
   tipo?: string;
   gerado_em?: string;
   inclui_dados_sensiveis?: boolean;
   cliente_id?: string;
   [key: string]: any;
 };
 message?: string;
 timestamp?: string;
 [key: string]: any;
}

class SimpleRequestManager {
 private cancelTokens = new Map<string, CancelTokenSource>();

 async makeRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
   this.cancelRequest(key);
   const cancelToken = axios.CancelToken.source();
   this.cancelTokens.set(key, cancelToken);

   try {
     const result = await requestFn();
     return result;
   } finally {
     this.cancelTokens.delete(key);
   }
 }

 cancelRequest(key: string): void {
   const cancelToken = this.cancelTokens.get(key);
   if (cancelToken) {
     cancelToken.cancel(`Request ${key} cancelled`);
     this.cancelTokens.delete(key);
   }
 }

 cancelAllRequests(): void {
   Array.from(this.cancelTokens.values()).forEach(token => {
     token.cancel('All requests cancelled');
   });
   this.cancelTokens.clear();
 }
}

const createAxiosInstance = () => {
 const instance = axios.create({
   baseURL: `${API_BASE_URL}${MAPEAMENTOS_API_PREFIX}`,
   headers: {
     'Content-Type': 'application/json',
   },
   timeout: 45000,
   validateStatus: (status) => status < 500,
 });

 instance.interceptors.request.use(
   (config) => {
     const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
     if (token) {
       config.headers = config.headers || {};
       config.headers.Authorization = `Bearer ${token}`;
     }
     
     return config;
   },
   (error) => Promise.reject(error)
 );

 instance.interceptors.response.use(
   (response) => {
     return response;
   },
   (error) => {
     if (axios.isCancel(error)) {
       return Promise.reject(error);
     }
     
     if (error.response?.status === 401) {
       localStorage.removeItem('authToken');
       sessionStorage.removeItem('authToken');
       if (!window.location.pathname.includes('/login')) {
         window.location.href = '/login';
       }
     }

     const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Erro desconhecido';
     
     error.friendlyMessage = errorMessage;
     return Promise.reject(error);
   }
 );

 return instance;
};

class MapeamentosService {
 private requestManager = new SimpleRequestManager();
 private api = createAxiosInstance();

 private validateId(id: string, fieldName: string = 'ID'): void {
   if (!id || id.trim() === '') {
     throw new Error(`${fieldName} é obrigatório`);
   }
 }

 private validateRequired(data: any, fields: string[]): void {
   const missing = fields.filter(field => !data[field] && data[field] !== 0);
   if (missing.length > 0) {
     throw new Error(`Campos obrigatórios: ${missing.join(', ')}`);
   }
 }

 private handleApiResponse<T>(response: AxiosResponse<any>): T {
   if (response.data && typeof response.data === 'object') {
     if ('success' in response.data) {
       if (!response.data.success) {
         throw new Error(response.data.error || response.data.message || 'Erro na API');
       }
       return response.data.data;
     }
     return response.data;
   }
   throw new Error('Resposta da API em formato inesperado');
 }

 private getDefaultPagination(page: number, limit: number): PaginationMeta {
   return {
     page,
     limit,
     total: 0,
     pages: 0,
     hasNext: false,
     hasPrev: false
   };
 }

 private buildClienteParams(clienteId?: string, otherParams: any = {}): any {
   const params = { ...otherParams };
   
   if (clienteId && clienteId.trim() !== '') {
     params.cliente_id = clienteId.trim();
   }
   
   return params;
 }

 private getEstatisticasVazias(): EstatisticasGerais {
   return {
     total_questionarios: 0,
     questionarios_concluidos: 0,
     total_titulares: 0,
     categorias_titulares: 0,
     dados_sensiveis: 0,
     dados_comuns: 0,
     areas_mapeadas: 0,
     total_processos: 0,
     total_perguntas: 0,
     taxa_conclusao: 0
   };
 }

 private processarEstatisticas(rawData: any): EstatisticasGerais {
   try {
     const estatisticasBase = this.getEstatisticasVazias();
     
     if (!rawData) {
       return estatisticasBase;
     }
     
     if (typeof rawData !== 'object') {
       return estatisticasBase;
     }
     
     const dadosParaProcessar = rawData.data || rawData;
     
     const toSafeInteger = (value: any): number => {
       try {
         if (value === null || value === undefined) {
           return 0;
         }
         
         const stringValue = String(value).trim();
         
         if (stringValue === '' || stringValue === 'null' || stringValue === 'undefined') {
           return 0;
         }
         
         const parsedValue = parseInt(stringValue);
         
         if (isNaN(parsedValue)) {
           return 0;
         }
         
         return Math.max(0, parsedValue);
       } catch (error: any) {
         return 0;
       }
     };
     
     const toSafeFloat = (value: any, min: number = 0, max: number = 100): number => {
       try {
         if (value === null || value === undefined) {
           return min;
         }
         
         const stringValue = String(value).trim();
         
         if (stringValue === '' || stringValue === 'null' || stringValue === 'undefined') {
           return min;
         }
         
         const parsedValue = parseFloat(stringValue);
         
         if (isNaN(parsedValue)) {
           return min;
         }
         
         return Math.max(min, Math.min(max, parsedValue));
       } catch (error: any) {
         return min;
       }
     };
     
     const estatisticas: EstatisticasGerais = {
       total_questionarios: toSafeInteger(dadosParaProcessar.total_questionarios),
       questionarios_concluidos: toSafeInteger(dadosParaProcessar.questionarios_concluidos),
       total_titulares: toSafeInteger(dadosParaProcessar.total_titulares),
       categorias_titulares: toSafeInteger(dadosParaProcessar.categorias_titulares),
       dados_sensiveis: toSafeInteger(dadosParaProcessar.dados_sensiveis),
       dados_comuns: toSafeInteger(dadosParaProcessar.dados_comuns),
       areas_mapeadas: toSafeInteger(dadosParaProcessar.areas_mapeadas),
       total_processos: toSafeInteger(dadosParaProcessar.total_processos),
       total_perguntas: toSafeInteger(dadosParaProcessar.total_perguntas),
       taxa_conclusao: toSafeFloat(dadosParaProcessar.taxa_conclusao, 0, 100)
     };
     
     if (estatisticas.questionarios_concluidos > estatisticas.total_questionarios) {
       estatisticas.questionarios_concluidos = estatisticas.total_questionarios;
     }
     
     if (estatisticas.total_questionarios > 0) {
       const taxaCalculada = Math.round((estatisticas.questionarios_concluidos / estatisticas.total_questionarios) * 100);
       const diferencaPercent = Math.abs(estatisticas.taxa_conclusao - taxaCalculada);
       
       if (diferencaPercent > 5 || estatisticas.taxa_conclusao > 100 || estatisticas.taxa_conclusao < 0) {
         estatisticas.taxa_conclusao = taxaCalculada;
       }
     } else if (estatisticas.taxa_conclusao > 0) {
       estatisticas.taxa_conclusao = 0;
     }
     
     if (estatisticas.categorias_titulares > estatisticas.total_titulares && estatisticas.total_titulares > 0) {
       estatisticas.categorias_titulares = Math.min(estatisticas.categorias_titulares, estatisticas.total_titulares);
     }
     
     if (estatisticas.categorias_titulares > 0 && estatisticas.total_titulares === 0) {
       estatisticas.categorias_titulares = 0;
     }
     
     return estatisticas;
     
   } catch (error: unknown) {
     return this.getEstatisticasVazias();
   }
 }

 private normalizarDadosRelatorio(result: any): any {
   try {
     let dados: any = {};
     let metadata: any = {};

     if (result && typeof result === 'object') {
       if ('data' in result) {
         dados = result.data || {};
         metadata = result.metadata || {};
       } 
       else {
         dados = { ...result };
         if ('metadata' in result && result.metadata) {
           metadata = result.metadata;
           delete dados.metadata;
         }
       }
     }

     const dadosNormalizados = {
       dados: dados || {},
       metadata: {
         gerado_em: new Date().toISOString(),
         possui_dados: Boolean(dados && Object.keys(dados).length > 0),
         estrutura_normalizada: true,
         ...metadata
       }
     };

     return dadosNormalizados;
   } catch (error: unknown) {
     const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
     
     return {
       dados: {},
       metadata: {
         gerado_em: new Date().toISOString(),
         possui_dados: false,
         erro: true,
         erro_normalizacao: errorMessage
       }
     };
   }
 }

 async listarQuestionarios(
   filtros: FiltroQuestionarios = {},
   page: number = 1,
   limit: number = 20
 ): Promise<Questionario[] | { data: Questionario[]; pagination: PaginationMeta }> {
   const params = this.buildClienteParams(filtros.cliente_id, { 
     ...filtros, 
     page, 
     limit 
   });

   try {
     const response = await this.api.get('/questionarios', { params });
     return this.handleApiResponse(response);
   } catch (error: any) {
     return { data: [], pagination: this.getDefaultPagination(page, limit) };
   }
 }

 async criarQuestionario(dados: CreateQuestionarioDTO): Promise<Questionario> {
   this.validateRequired(dados, ['nome']);
   
   const response = await this.api.post('/questionarios', dados);
   return this.handleApiResponse<Questionario>(response);
 }

 async obterQuestionario(id: string): Promise<QuestionarioDetalhado> {
   this.validateId(id, 'ID do questionário');
   const response = await this.api.get(`/questionarios/${id}`);
   return this.handleApiResponse<QuestionarioDetalhado>(response);
 }

 async atualizarQuestionario(id: string, dados: Partial<CreateQuestionarioDTO>): Promise<Questionario> {
   this.validateId(id, 'ID do questionário');
   const response = await this.api.put(`/questionarios/${id}`, dados);
   return this.handleApiResponse<Questionario>(response);
 }

 async removerQuestionario(id: string): Promise<void> {
   this.validateId(id, 'ID do questionário');
   await this.api.delete(`/questionarios/${id}`);
 }

 async duplicarQuestionario(questionarioId: string, novoNome: string): Promise<Questionario> {
   this.validateId(questionarioId, 'ID do questionário');
   
   if (!novoNome || novoNome.trim() === '') {
     throw new Error('Nome do novo questionário é obrigatório');
   }

   const response = await this.api.post(`/questionarios/${questionarioId}/duplicar`, {
     nome: novoNome.trim()
   });
   
   return this.handleApiResponse<Questionario>(response);
 }

 async vincularCapitulo(dados: VincularCapituloDTO): Promise<QuestionarioCapitulo> {
   this.validateRequired(dados, ['questionario_id', 'capitulo_id']);
   const response = await this.api.post('/capitulos/vincular', dados);
   return this.handleApiResponse<QuestionarioCapitulo>(response);
 }

 async listarPerguntasCapitulo(capituloId: string): Promise<Pergunta[]> {
   this.validateId(capituloId, 'ID do capítulo');
   const response = await this.api.get(`/capitulos/${capituloId}/perguntas`);
   return this.handleApiResponse<Pergunta[]>(response);
 }

 async criarVersaoRespostas(dados: CreateVersaoRespostasDTO): Promise<VersaoRespostas> {
   this.validateRequired(dados, ['questionario_id']);
   const response = await this.api.post('/versoes', dados);
   return this.handleApiResponse<VersaoRespostas>(response);
 }

 async atualizarStatusVersao(id: string, dados: UpdateStatusVersaoDTO): Promise<VersaoRespostas> {
   this.validateId(id, 'ID da versão');
   this.validateRequired(dados, ['status']);
   const response = await this.api.put(`/versoes/${id}/status`, dados);
   return this.handleApiResponse<VersaoRespostas>(response);
 }

 async salvarResposta(dados: SalvarRespostaDTO): Promise<Resposta> {
   this.validateRequired(dados, ['questionario_perguntas_id', 'user_id', 'versao_respostas']);
   const response = await this.api.post('/respostas', dados);
   return this.handleApiResponse<Resposta>(response);
 }

 async criarTitular(dados: CreateTitularDTO): Promise<Titular> {
   const response = await this.api.post('/titulares', dados);
   return this.handleApiResponse<Titular>(response);
 }

 async listarTitulares(
   filtros: FiltroTitulares = {},
   page: number = 1,
   limit: number = 20
 ): Promise<TitularDetalhado[] | { data: TitularDetalhado[]; pagination: PaginationMeta }> {
   const params = this.buildClienteParams(filtros.cliente_id, { 
     ...filtros, 
     page, 
     limit 
   });

   try {
     const response = await this.api.get('/titulares', { params });
     return this.handleApiResponse(response);
   } catch (error: any) {
     return { data: [], pagination: this.getDefaultPagination(page, limit) };
   }
 }

 async obterTitular(id: string): Promise<TitularDetalhado> {
   this.validateId(id, 'ID do titular');
   const response = await this.api.get(`/titulares/${id}`);
   return this.handleApiResponse<TitularDetalhado>(response);
 }

 async atualizarTitular(id: string, dados: Partial<CreateTitularDTO>): Promise<Titular> {
   this.validateId(id, 'ID do titular');
   const response = await this.api.put(`/titulares/${id}`, dados);
   return this.handleApiResponse<Titular>(response);
 }

 async removerTitular(id: string): Promise<void> {
   this.validateId(id, 'ID do titular');
   await this.api.delete(`/titulares/${id}`);
 }

 async obterRelatorioMapeamento(questionarioId: string, clienteId?: string): Promise<RelatorioMapeamento> {
   this.validateId(questionarioId, 'ID do questionário');
   const params = this.buildClienteParams(clienteId);
   const response = await this.api.get(`/relatorios/${questionarioId}`, { params });
   return this.handleApiResponse<RelatorioMapeamento>(response);
 }

 async gerarDadosRelatorio(config: {
   tipo: string;
   questionario_id?: string;
   incluir_dados_sensiveis?: boolean;
   cliente_id?: string;
 }): Promise<any> {
   try {
     const params = this.buildClienteParams(config.cliente_id);
     
     const payload = {
       tipo: config.tipo,
       questionario_id: config.questionario_id,
       incluir_dados_sensiveis: config.incluir_dados_sensiveis ?? true
     };
     
     const response = await this.api.post('/relatorios/gerar', payload, { 
       params,
       timeout: 60000
     });

     const result = this.handleApiResponse<ApiRelatorioResponse>(response);
     const dadosNormalizados = this.normalizarDadosRelatorio(result);
     
     return dadosNormalizados;
     
   } catch (error: any) {
     return {
       dados: {},
       metadata: {
         tipo: config.tipo,
         gerado_em: new Date().toISOString(),
         inclui_dados_sensiveis: config.incluir_dados_sensiveis ?? true,
         cliente_id: config.cliente_id,
         erro: true,
         erro_detalhes: error.message
       }
     };
   }
 }

 async validarDadosRelatorio(config: {
   tipo: string;
   questionario_id?: string;
   cliente_id?: string;
 }): Promise<any> {
   try {
     const params = this.buildClienteParams(config.cliente_id);
     
     const response = await this.api.post('/relatorios/validar', {
       tipo: config.tipo,
       questionario_id: config.questionario_id
     }, { params });
     
     const result = this.handleApiResponse<any>(response);
     
     return result;
   } catch (error: any) {
     return {
       valido: true,
       avisos: ['Não foi possível validar os dados, mas o relatório pode ser gerado'],
       erros: [],
       estatisticas_previas: {},
       recomendacoes: []
     };
   }
 }

 async listarAreas(
   filtros: FiltroAreas = {},
   page: number = 1,
   limit: number = 50
 ): Promise<Area[] | { data: Area[]; pagination: PaginationMeta }> {
   const params = this.buildClienteParams(filtros.cliente_id, { 
     ...filtros, 
     page, 
     limit 
   });

   try {
     const response = await this.api.get('/areas', { params });
     return this.handleApiResponse(response);
   } catch (error: any) {
     return { data: [], pagination: this.getDefaultPagination(page, limit) };
   }
 }

 async listarDados(
   filtros: FiltroDados = {},
   page: number = 1,
   limit: number = 50
 ): Promise<Dado[] | { data: Dado[]; pagination: PaginationMeta }> {
  const params = { ...filtros, page, limit };

  try {
    const response = await this.api.get('/dados', { params });
    return this.handleApiResponse(response);
  } catch (error: any) {
    return { data: [], pagination: this.getDefaultPagination(page, limit) };
  }
}

async listarProcessos(
  filtros: any = {},
  page: number = 1,
  limit: number = 50
): Promise<any[] | { data: any[]; pagination: PaginationMeta }> {
  const params = this.buildClienteParams(filtros.cliente_id, { 
    ...filtros, 
    page, 
    limit 
  });

  try {
    const response = await this.api.get('/processos', { params });
    return this.handleApiResponse(response);
  } catch (error: any) {
    return { data: [], pagination: this.getDefaultPagination(page, limit) };
  }
}

async obterEstatisticas(clienteId?: string): Promise<{
  success: boolean;
  data: EstatisticasGerais;
  message?: string;
  timestamp?: string;
}> {
  try {
    const params = this.buildClienteParams(clienteId);
    const response = await this.api.get('/estatisticas', { 
      params,
      timeout: 30000
    });
    
    const rawResult = this.handleApiResponse<ApiEstatisticasResponse>(response);

    let dadosEstatisticas: any = {};

    if (rawResult && typeof rawResult === 'object') {
      if ('success' in rawResult && 'data' in rawResult && rawResult.data) {
        dadosEstatisticas = rawResult.data;
      } else {
        dadosEstatisticas = rawResult;
      }
    }

    const estatisticas = this.processarEstatisticas(dadosEstatisticas);

    return {
      success: true,
      data: estatisticas,
      message: 'Estatísticas obtidas com sucesso',
      timestamp: new Date().toISOString()
    };

  } catch (error: any) {
    return {
      success: false,
      data: this.getEstatisticasVazias(),
      message: `Erro ao obter estatísticas: ${error.message}`,
      timestamp: new Date().toISOString()
    };
  }
}

async buscarAreas(termo: string, clienteId?: string): Promise<Area[]> {
  if (!termo || termo.trim() === '') {
    throw new Error('Termo de busca é obrigatório');
  }

  const params = this.buildClienteParams(clienteId, { search: termo.trim() });
  const response = await this.api.get('/areas', { params });
  return this.handleApiResponse<Area[]>(response);
}

async buscarProcessos(termo: string, clienteId?: string): Promise<any[]> {
  if (!termo || termo.trim() === '') {
    throw new Error('Termo de busca é obrigatório');
  }

  const params = this.buildClienteParams(clienteId, { search: termo.trim() });
  const response = await this.api.get('/processos', { params });
  return this.handleApiResponse<any[]>(response);
}

async buscarDados(termo: string, tipo?: string): Promise<Dado[]> {
  if (!termo || termo.trim() === '') {
    throw new Error('Termo de busca é obrigatório');
  }

  const params = {
    search: termo.trim(),
    ...(tipo && { tipo }),
  };

  const response = await this.api.get('/dados', { params });
  return this.handleApiResponse<Dado[]>(response);
}

async preValidarRelatorio(tipo: string, clienteId?: string, questionarioId?: string): Promise<{
  temDados: boolean;
  avisos: string[];
  recomendacoes: string[];
  estatisticas: EstatisticasGerais;
}> {
  try {
    const statsResponse = await this.obterEstatisticas(clienteId);
    const stats = statsResponse.data;
    
    const avisos: string[] = [];
    const recomendacoes: string[] = [];
    
    switch (tipo) {
      case 'geral':
        if (stats.total_questionarios === 0) {
          avisos.push('Nenhum questionário encontrado');
          recomendacoes.push('Cadastre questionários antes de gerar o relatório');
        }
        if (stats.total_titulares === 0) {
          avisos.push('Nenhum titular encontrado');
          recomendacoes.push('Cadastre titulares para enriquecer o relatório');
        }
        break;
        
      case 'questionario_detalhado':
        if (!questionarioId) {
          avisos.push('ID do questionário não fornecido');
          recomendacoes.push('Selecione um questionário específico');
        }
        break;
        
      case 'titulares':
        if (stats.total_titulares === 0) {
          avisos.push('Nenhum titular encontrado');
          recomendacoes.push('Cadastre titulares antes de gerar este relatório');
        }
        break;
        
      case 'estatisticas':
        break;
    }
    
    const temDados = stats.total_questionarios > 0 || stats.total_titulares > 0 || tipo === 'estatisticas';
    
    return {
      temDados,
      avisos,
      recomendacoes,
      estatisticas: stats
    };
    
  } catch (error: any) {
    return {
      temDados: false,
      avisos: ['Erro ao validar dados'],
      recomendacoes: ['Verifique a conectividade e tente novamente'],
      estatisticas: this.getEstatisticasVazias()
    };
  }
}

async obterPreviewRelatorio(tipo: string, clienteId?: string, questionarioId?: string): Promise<{
  preview: any;
  estatisticas: EstatisticasGerais;
  metadata: any;
}> {
  try {
    const [preValidacao, dadosLimitados] = await Promise.allSettled([
      this.preValidarRelatorio(tipo, clienteId, questionarioId),
      this.gerarDadosRelatorio({
        tipo,
        cliente_id: clienteId,
        questionario_id: questionarioId,
        incluir_dados_sensiveis: false
      })
    ]);
    
    const validacao = preValidacao.status === 'fulfilled' ? preValidacao.value : {
      temDados: false,
      avisos: ['Erro na validação'],
      recomendacoes: [],
      estatisticas: this.getEstatisticasVazias()
    };
    
    const dados = dadosLimitados.status === 'fulfilled' ? dadosLimitados.value : {
      dados: {},
      metadata: { erro: true }
    };
    
    const preview = {
      tipo,
      tem_dados: validacao.temDados,
      avisos: validacao.avisos,
      recomendacoes: validacao.recomendacoes,
      amostra_dados: this.criarAmostraDados(dados.dados),
      contadores: this.extrairContadores(dados.dados),
      estimativa_tamanho: this.estimarTamanhoRelatorio(dados.dados)
    };
    
    return {
      preview,
      estatisticas: validacao.estatisticas,
      metadata: {
        ...dados.metadata,
        preview_gerado_em: new Date().toISOString()
      }
    };
    
  } catch (error: any) {
    return {
      preview: {
        tipo,
        tem_dados: false,
        erro: error.message
      },
      estatisticas: this.getEstatisticasVazias(),
      metadata: {
        erro: true,
        erro_detalhes: error.message
      }
    };
  }
}

private criarAmostraDados(dados: any): any {
  const amostra: any = {};
  
  try {
    if (dados.questionarios && Array.isArray(dados.questionarios)) {
      amostra.questionarios = dados.questionarios.slice(0, 3).map((q: any) => ({
        nome: q.nome,
        abordagem: q.abordagem,
        ativo: !q.inativo
      }));
    }
    
    if (dados.titulares && Array.isArray(dados.titulares)) {
      amostra.titulares = dados.titulares.slice(0, 3).map((t: any) => ({
        categoria: t.categoria,
        quantidade: t.quantidade,
        tipos_dados: Array.isArray(t.dados) ? t.dados.length : 0
      }));
    }
    
    if (dados.estatisticas) {
      amostra.estatisticas = dados.estatisticas;
    }
    
  } catch (error) {
    // Silently handle error
  }
  
  return amostra;
}

private extrairContadores(dados: any): any {
  const contadores: any = {};
  
  try {
    contadores.questionarios = Array.isArray(dados.questionarios) ? dados.questionarios.length : 0;
    contadores.titulares = Array.isArray(dados.titulares) ? dados.titulares.length : 0;
    contadores.areas = Array.isArray(dados.areas) ? dados.areas.length : 0;
    contadores.processos = Array.isArray(dados.processos) ? dados.processos.length : 0;
   contadores.tipos_dados = Array.isArray(dados.tipos_dados) ? dados.tipos_dados.length : 0;
   
   if (Array.isArray(dados.titulares)) {
     contadores.titulares_com_dados_sensiveis = dados.titulares.filter((t: any) => 
       Array.isArray(t.dados) && t.dados.some((d: any) => 
         typeof d.dado_id === 'object' && d.dado_id?.tipo === 'sensivel'
       )
     ).length;
   }
   
 } catch (error) {
   // Silently handle error
 }
 
 return contadores;
}

private estimarTamanhoRelatorio(dados: any): string {
 try {
   const jsonSize = JSON.stringify(dados).length;
   
   if (jsonSize < 1000) return 'Pequeno (< 1KB)';
   if (jsonSize < 10000) return 'Médio (< 10KB)';
   if (jsonSize < 100000) return 'Grande (< 100KB)';
   return 'Muito Grande (> 100KB)';
   
 } catch (error) {
   return 'Indeterminado';
 }
}

cancelAllRequests(): void {
 this.requestManager.cancelAllRequests();
}

async healthCheck(): Promise<{ status: string; timestamp: string; details?: any }> {
 try {
   const response = await this.api.get('/health', { timeout: 10000 });
   const result = this.handleApiResponse(response);
   
   return {
     status: 'healthy',
     timestamp: new Date().toISOString(),
     details: result
   };
   
 } catch (error: any) {
   throw new Error(`Serviço indisponível: ${error.friendlyMessage || error.message}`);
 }
}

async testarConectividade(): Promise<boolean> {
 try {
   await this.healthCheck();
   return true;
 } catch (error) {
   return false;
 }
}

limparCache(): void {
 this.cancelAllRequests();
}

utils = {
 formatarData: (data: string | Date): string => {
   if (!data) return 'N/A';
   try {
     const dataObj = typeof data === 'string' ? new Date(data) : data;
     return new Intl.DateTimeFormat('pt-BR', {
       day: '2-digit',
       month: '2-digit',
       year: 'numeric',
       hour: '2-digit',
       minute: '2-digit'
     }).format(dataObj);
   } catch {
     return 'Data inválida';
   }
 },

 calcularTempoDecorrido: (data: string | Date): string => {
   if (!data) return 'N/A';
   
   try {
     const agora = new Date();
     const dataObj = typeof data === 'string' ? new Date(data) : data;
     const diffMs = agora.getTime() - dataObj.getTime();
     const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
     
     if (diffDias === 0) return 'Hoje';
     if (diffDias === 1) return 'Ontem';
     if (diffDias < 7) return `${diffDias} dias atrás`;
     if (diffDias < 30) return `${Math.floor(diffDias / 7)} semanas atrás`;
     if (diffDias < 365) return `${Math.floor(diffDias / 30)} meses atrás`;
     return `${Math.floor(diffDias / 365)} anos atrás`;
   } catch {
     return 'Data inválida';
   }
 },

 calcularPorcentagemProgresso: (completos: number, total: number): number => {
   if (total === 0) return 0;
   return Math.round((completos / total) * 100);
 },

 sanitizarTexto: (texto: string): string => {
   if (!texto) return '';
   return texto.trim().replace(/[<>]/g, '');
 },

 formatarStatus: (status: string): string => {
   const statusMap: Record<string, string> = {
     'Pendente': 'Pendente',
     'Enviado': 'Enviado',
     'Aguardando respostas': 'Aguardando Respostas',
     'Concluído': 'Concluído'
   };
   return statusMap[status] || status;
 },

 getCorStatus: (status: string): string => {
   const statusColorMap: Record<string, string> = {
     'Pendente': '#faad14',
     'Enviado': '#1890ff',
     'Aguardando respostas': '#722ed1',
     'Concluído': '#52c41a'
   };
   return statusColorMap[status] || '#d9d9d9';
 },

 formatarTipoDado: (tipo: string): string => {
   const tipoMap: Record<string, string> = {
     'sensivel': 'Sensível',
     'comum': 'Comum'
   };
   return tipoMap[tipo] || tipo;
 },

 formatarTamanhoArquivo: (bytes: number): string => {
   if (bytes === 0) return '0 Bytes';
   
   const k = 1024;
   const sizes = ['Bytes', 'KB', 'MB', 'GB'];
   const i = Math.floor(Math.log(bytes) / Math.log(k));
   
   return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
 },

 validarEmail: (email: string): boolean => {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return emailRegex.test(email);
 },

 validarCNPJ: (cnpj: string): boolean => {
   const cnpjLimpo = cnpj.replace(/[^\d]/g, '');
   return cnpjLimpo.length === 14;
 },

 debounce: <T extends (...args: any[]) => any>(
   func: T,
   wait: number
 ): ((...args: Parameters<T>) => void) => {
   let timeout: NodeJS.Timeout;
   return (...args: Parameters<T>) => {
     clearTimeout(timeout);
     timeout = setTimeout(() => func.apply(null, args), wait);
   };
 },

 formatarEstatisticasParaDebug: (stats: EstatisticasGerais): string => {
   return [
     `Q: ${stats.questionarios_concluidos}/${stats.total_questionarios} (${stats.taxa_conclusao}%)`,
     `T: ${stats.total_titulares} em ${stats.categorias_titulares} cats`,
     `D: ${stats.dados_sensiveis}S + ${stats.dados_comuns}C`,
     `I: ${stats.areas_mapeadas}A ${stats.total_processos}P ${stats.total_perguntas}Pg`
   ].join(' | ');
 },

 criarResumoRelatorio: (dados: any): string => {
   try {
     const parts: string[] = [];
     
     if (dados.questionarios?.length) parts.push(`${dados.questionarios.length} questionários`);
     if (dados.titulares?.length) parts.push(`${dados.titulares.length} titulares`);
     if (dados.areas?.length) parts.push(`${dados.areas.length} áreas`);
     if (dados.processos?.length) parts.push(`${dados.processos.length} processos`);
     
     return parts.length > 0 ? parts.join(', ') : 'Sem dados';
   } catch {
     return 'Erro ao processar resumo';
   }
 }
};
}

export const mapeamentosService = new MapeamentosService();
export default mapeamentosService;